import React from 'react';
import { Box, Button, Paper, TextField, Typography, CircularProgress } from '@mui/material';
import { fetchGusByNip, GusBasicData } from '../services/gusService';

const GusPage: React.FC = () => {
  const [nip, setNip] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [result, setResult] = React.useState<GusBasicData | null>(null);

  const onFetch = async () => {
    const clean = (nip || '').replace(/[^0-9]/g, '');
    setError('');
    setResult(null);
    if (!clean || clean.length < 10) {
      setError('Podaj poprawny NIP (10 cyfr)');
      return;
    }
    setLoading(true);
    try {
      const data = await fetchGusByNip(clean);
      setResult(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Nie udało się pobrać danych z GUS');
    } finally {
      setLoading(false);
    }
  };

  const getRodzajDzialalnosci = (typ?: string) => {
    if (!typ) return '—';
    const map: Record<string, string> = {
      'P': 'Spółka z ograniczoną odpowiedzialnością',
      'F': 'Jednoosobowa działalność gospodarcza (JDG)',
      'LP': 'Spółka partnerska',
      'LF': 'Spółka komandytowa',
      'L': 'Spółka komandytowo-akcyjna',
      'S': 'Spółka akcyjna',
      'C': 'Spółka cywilna',
    };
    return map[typ] || typ;
  };

  const formatAddress = (d?: GusBasicData | null) => {
    if (!d) return '—';
    const parts: string[] = [];
    
    // Ulica + numer
    const ulicaParts = [d.ulica, d.nrNieruchomosci].filter(Boolean);
    if (ulicaParts.length > 0) {
      parts.push(ulicaParts.join(' '));
    }
    
    // Kod pocztowy + Miejscowość
    const miejscowosc = d.miejscowoscPoczty || d.miejscowosc || '';
    const kodMiejscowosc = [d.kodPocztowy, miejscowosc].filter(Boolean).join(' ');
    if (kodMiejscowosc) {
      parts.push(kodMiejscowosc);
    }
    
    return parts.length > 0 ? parts.join(', ') : '—';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>GUS – pobieranie danych (BIR 1.1)</Typography>
      <Paper sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', maxWidth: 720 }}>
        <TextField
          label="NIP"
          value={nip}
          onChange={(e) => setNip(e.target.value)}
          placeholder="np. 5210082542"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 20 }}
          sx={{ width: 280 }}
        />
        <Button variant="contained" onClick={onFetch} disabled={loading}>
          {loading ? <><CircularProgress size={18} sx={{ mr: 1 }} /> Pobieranie...</> : 'Pobierz z GUS'}
        </Button>
        {error && <Typography color="error" sx={{ ml: 1 }}>{error}</Typography>}
      </Paper>

      {result && (
        <Box sx={{ mt: 3, maxWidth: 720 }}>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>Podstawowe informacje</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography>
              <strong>1. Rodzaj działalności:</strong> {getRodzajDzialalnosci(result.typ)}
            </Typography>
            <Typography>
              <strong>2. Nazwa:</strong> {result.nazwa || '—'}
            </Typography>
            <Typography>
              <strong>3. Adres:</strong> {formatAddress(result)}
            </Typography>
            <Typography>
              <strong>4. Województwo:</strong> {result.wojewodztwo || '—'}
            </Typography>
            <Typography>
              <strong>5. NIP:</strong> {result.nip || '—'}
            </Typography>
            <Typography>
              <strong>6. REGON:</strong> {result.regon || '—'}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GusPage;
